import React from 'react';
import './Sidebar.css';
import { MODULES, ModuleMeta } from '../navigation/modulesConfig';
import { useAuth } from '../../contexts/AuthContext';
import { ROLE_ORG_MEMBER, ROLE_AUDIT_USER, ROLE_RISK_ANALYST } from '../../services/userService';
import { ChevronLeft, Menu, Shield } from 'lucide-react';

interface MenuItem { id: string; label: string; icon: React.ReactNode; path: string; }

interface SidebarProps {
  isOpen: boolean;
  onMenuSelect: (menuId: string) => void;
  activeMenu: string;
  onToggle: () => void;
}

// Ordered to reflect logical GRC lifecycle while minimizing scroll
const menuItems: MenuItem[] = MODULES
  .filter((m: ModuleMeta) => m.showInSidebar)
  .sort((a: ModuleMeta, b: ModuleMeta) => a.order - b.order)
  .map((m: ModuleMeta) => ({ id: m.id, label: m.label, icon: m.icon, path: m.path }));

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onMenuSelect, activeMenu, onToggle }) => {
  const { state } = useAuth();

  const userRoleStr = state.user?.role?.toString() || sessionStorage.getItem('anygrc_user_role');
  const userRole = userRoleStr ? parseInt(userRoleStr, 10) : null;

  // Hide sidebar for roles that don't use it
  const hideSidebar = userRole === ROLE_ORG_MEMBER || userRole === ROLE_AUDIT_USER || userRole === ROLE_RISK_ANALYST;

  const handleMenuClick = (item: MenuItem) => {
    onMenuSelect(item.id);
  };

  if (hideSidebar) {
    return null;
  }

  return (
    <>
      {isOpen && <div className="sidebar-overlay" />}
      {/* No roleClass — sidebar background comes from var(--nav-sidebar-bg) via [data-role] */}
      <nav className={`sidebar ${isOpen ? 'expanded' : 'collapsed'}`}>
        <div className="sidebar-content">
          {isOpen && (
            <div className="sidebar-header">
              <div className="sidebar-logo">
                {isOpen ? (
                  <span className="logo-text">AnyGRC</span>
                ) : (
                  /* Lucide icon instead of emoji */
                  <Shield size={20} aria-hidden="true" />
                )}
              </div>
              <button data-testid="BTN_sidebar_toggle" className="sidebar-toggle-btn" onClick={onToggle} aria-label={isOpen ? 'Collapse sidebar' : 'Expand sidebar'}>
                {isOpen ? <ChevronLeft size={20} /> : <Menu size={20} />}
              </button>
            </div>
          )}

          <ul className="sidebar-menu">
            {menuItems.map((item) => (
              <li key={item.id} className="menu-item">
                <button
                  data-testid={`BTN_sidebar_menu_${item.id}`}
                  className={`menu-button ${activeMenu === item.id ? 'active' : ''}`}
                  onClick={() => handleMenuClick(item)}
                  title={!isOpen ? item.label : undefined}
                  aria-current={activeMenu === item.id ? 'page' : undefined}
                >
                  <span className="menu-icon" aria-hidden="true">{item.icon}</span>
                  {isOpen && <span className="menu-label">{item.label}</span>}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {isOpen && (
          <div className="sidebar-footer">
            <div className="system-status">
              <div className="status-indicator online" aria-hidden="true"></div>
              <span>System Online</span>
            </div>
            <div className="version-info">
              <small>v1.0.0</small>
            </div>
          </div>
        )}
      </nav>
    </>
  );
};

export default Sidebar;